// StagedDrawer — the drawer frame of every STAGED draft rather than a form: an ordered chain, a reorder, a terms list
// (ADR-038 — the container follows the act). The slide-over composed with the staged guard (`useStagedGuard`): every
// close path (✕ · backdrop · Escape · Cancel) confirms first while the draft is dirty, a busy save never closes, a
// refresh warns, and the footer is handed the guard so a host's own leaving act (Back to a list) confirms the same
// way. The draft stays the host's (`dirty` is its word); `useResetOnOpen` (hooks/) is the reseed-on-open contract.
// A refused save is the host's `useRefusalLine` handed in as `line` (ADR-084 §6): the frame draws it first in the
// body — a stale save offers Load the latest, which REPLACES the draft with the fresh read (a list cannot keep the
// typed values and take the rest); a row gone under the draft offers Close, the guard standing down.

import { useEffect, type ReactNode } from 'react';
import { SlideOver } from '../ui/SlideOver.js';
import { RefusalLine } from './forms/RefusalLine.js';
import type { RefusalLineState } from './forms/useRefusalLine.js';
import { useStagedGuard, type StagedGuard } from './useStagedGuard.js';

export type { StagedGuard } from './useStagedGuard.js';

export interface StagedDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** The host's word: the draft differs from what was read. */
  dirty: boolean;
  /** A save in flight — no close path acts. */
  busy?: boolean;
  /** The host's refusal line (`useRefusalLine({ edit: true, reload })`) — drawn first in the body. */
  line?: RefusalLineState;
  /** The footer, or a function of the guard (its Cancel and Back route through it). */
  footer?: ReactNode | ((guard: StagedGuard) => ReactNode);
  maxWidth?: string;
  children: ReactNode;
}

export function StagedDrawer({ open, onClose, title, description, dirty, busy = false, line, footer, maxWidth, children }: StagedDrawerProps) {
  const gone = line?.gone ?? false;
  const { discardDialog, ...guard } = useStagedGuard({ open, dirty: dirty && !gone, busy, onClose });
  // A host stays mounted between opens: the last attempt's line never greets the next open.
  const dismiss = line?.dismiss;
  useEffect(() => {
    if (open) dismiss?.();
  }, [open, dismiss]);
  return (
    <>
      <SlideOver open={open} onClose={guard.requestClose} title={title} description={description} footer={typeof footer === 'function' ? footer(guard) : footer} maxWidth={maxWidth}>
        {line && <RefusalLine line={line} kept={false} onClose={onClose} className="mb-4" />}
        {children}
      </SlideOver>
      {discardDialog}
    </>
  );
}
